// Story 11.5 — Wrapper que monta `CommandPalette` con context-awareness:
// detecta `botId` desde la ruta para ofrecer Files/Flows; copia comando
// CLI cuando se invoca `open-in-cli`. Vive dentro de `<RouterProvider>`
// porque depende de `useRouterState`.

import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CommandPalette, snippetIdFromActionId } from '@/components/CommandPalette';
import { findSnippet } from '@/lib/yaml-snippets';

// P-476 (review 11.5): regex anchored al inicio del path (`^/bots/...`)
// para no matchear hipotéticos `/admin/bots/<id>/audit` u otros paths
// donde un `/bots/<id>` aparezca embebido sin ser la ruta del editor.
const BOT_ID_REGEX = /^\/bots\/(\d+)(?:\/|$)/;

export function GlobalCommandPalette(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });

  const botMatch = path.match(BOT_ID_REGEX);
  const botId = botMatch ? Number(botMatch[1]) : null;

  // Cmd+K / Ctrl+K toggle global.
  // P-465 (review 11.5): SIN `capture: true` — la fase de captura corría
  // ANTES de Monaco's keydown y rompía multi-cursor / chord shortcuts del
  // editor. Bubble phase + skip cuando el target está dentro de Monaco o
  // de un input editable (P-479). El override del browser default (focus
  // URL bar) sigue funcionando vía `preventDefault()`.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmd = e.metaKey || e.ctrlKey;
      if (!isCmd || (e.key !== 'k' && e.key !== 'K')) return;
      // P-465: skip IME composition.
      if (e.isComposing) return;
      const target = e.target as HTMLElement | null;
      // P-465: skip si el foco está dentro del Monaco editor.
      if (target?.closest?.('.monaco-editor')) return;
      // P-479: skip cuando el user está escribiendo en un input/textarea
      // (search box, deploy notes, etc.) — el palette no debe robarles
      // el contexto.
      if (
        target != null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setOpen((v) => !v);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function handleAction(actionId: string): void {
    if (actionId === 'open-in-cli') {
      const cmd = botId ? `revel-bots dev --bot ${botId}` : 'revel-bots dev';
      void navigator.clipboard?.writeText(cmd);
      toast.success(`Copiado al portapapeles: ${cmd}`);
      return;
    }
    if (actionId === 'view-docs') {
      window.open('https://docs.revelation-bots.dev', '_blank', 'noopener,noreferrer');
      return;
    }
    const snippetId = snippetIdFromActionId(actionId);
    if (snippetId) {
      const snippet = findSnippet(snippetId);
      if (snippet) {
        // Story 11.5: el snippet se aplica al editor activo via custom event.
        // El CodeEditor escucha y dispara `editor.trigger('insertSnippet', ...)`.
        window.dispatchEvent(
          new CustomEvent('revel:insert-snippet', { detail: { body: snippet.body } }),
        );
      }
      return;
    }
    if (actionId === 'deploy' || actionId === 'rollback' || actionId === 'save-all') {
      // Re-dispatch a la EditorPage via custom event — esta es la única
      // forma sin acoplar el palette a la store de los dialogs.
      window.dispatchEvent(new CustomEvent(`revel:command-${actionId}`));
    }
  }

  return (
    <CommandPalette
      open={open}
      onClose={() => setOpen(false)}
      botId={botId}
      onAction={handleAction}
    />
  );
}
